/**
 * Bundle processor implementation using JSZip
 * Implements IBundleProcessor interface
 */

import JSZip from 'jszip';
import type {
  IBundleProcessor,
  BundleContent,
  BundleValidationResult,
  ValidationError,
  ValidationWarning,
} from '../../../contracts/bundle.ts';
import { getMimeType, getAssetType } from '../../../contracts/bundle.ts';
import type { Asset, AssetMap, BundleStructure } from '../../../contracts/book.ts';

const MAX_BUNDLE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file

export class BundleProcessor implements IBundleProcessor {
  async extract(file: File | ArrayBuffer): Promise<BundleContent> {
    const zip = new JSZip();
    const data = file instanceof File ? await file.arrayBuffer() : file;

    await zip.loadAsync(data);

    // Validate first
    const validation = await this.validate(file);

    if (!validation.valid) {
      throw new Error(
        `Invalid bundle: ${validation.errors.map((e) => e.message).join(', ')}`
      );
    }

    // Extract README.md
    const readmeFile = zip.file('README.md');
    if (!readmeFile) {
      throw new Error('README.md not found in bundle root');
    }
    const readme = await readmeFile.async('string');

    // Extract all assets
    const assets: AssetMap = {};
    const files: string[] = [];
    const directories = new Set<string>();
    let totalSize = 0;

    for (const [path, zipEntry] of Object.entries(zip.files)) {
      if (zipEntry.dir) {
        directories.add(path);
        continue;
      }

      // Skip README.md from assets
      if (path === 'README.md') {
        files.push(path);
        continue;
      }

      files.push(path);

      const mimeType = getMimeType(path);
      const type = getAssetType(mimeType);

      // Binary files as ArrayBuffer, text files as string
      const isBinary = type === 'image' || type === 'audio' || type === 'video';
      const assetData = isBinary
        ? await zipEntry.async('arraybuffer')
        : await zipEntry.async('string');

      const size = isBinary
        ? (assetData as ArrayBuffer).byteLength
        : (assetData as string).length;

      totalSize += size;

      const asset: Asset = {
        path,
        type,
        data: assetData,
        mimeType,
        size,
      };

      assets[path] = asset;

      // Track directories
      const dirPath = path.substring(0, path.lastIndexOf('/'));
      if (dirPath) {
        directories.add(dirPath + '/');
      }
    }

    const structure: BundleStructure = {
      files,
      directories: Array.from(directories),
      totalSize,
    };

    return {
      readme,
      assets,
      structure,
      validation,
    };
  }

  async validate(file: File | ArrayBuffer): Promise<BundleValidationResult> {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    try {
      const zip = new JSZip();
      const data = file instanceof File ? await file.arrayBuffer() : file;

      // Check total size
      if (data.byteLength > MAX_BUNDLE_SIZE) {
        errors.push({
          code: 'BUNDLE_TOO_LARGE',
          message: `Bundle size (${data.byteLength} bytes) exceeds maximum (${MAX_BUNDLE_SIZE} bytes)`,
          severity: 'error',
        });
      }

      await zip.loadAsync(data);

      // Check for README.md in root
      const readmeFile = zip.file('README.md');
      if (!readmeFile) {
        errors.push({
          code: 'MISSING_README',
          message: 'README.md not found in bundle root',
          severity: 'error',
        });
      }

      // Collect metadata
      let fileCount = 0;
      let directoryCount = 0;
      let totalSize = 0;
      const assetTypes = {
        images: 0,
        audio: 0,
        video: 0,
        documents: 0,
        other: 0,
      };

      for (const [path, zipEntry] of Object.entries(zip.files)) {
        if (zipEntry.dir) {
          directoryCount++;
          continue;
        }

        fileCount++;

        // Estimate file size (JSZip doesn't expose uncompressed size directly in public API)
        // We'll use a rough estimate based on the entry itself
        const size = (zipEntry as any)._data?.uncompressedSize ?? 0;
        totalSize += size;

        // Check individual file size
        if (size > MAX_FILE_SIZE) {
          warnings.push({
            code: 'FILE_TOO_LARGE',
            message: `File ${path} (${size} bytes) exceeds recommended size (${MAX_FILE_SIZE} bytes)`,
            file: path,
            severity: 'warning',
          });
        }

        // Categorize asset
        if (path !== 'README.md') {
          const mimeType = getMimeType(path);
          const type = getAssetType(mimeType);

          switch (type) {
            case 'image':
              assetTypes.images++;
              break;
            case 'audio':
              assetTypes.audio++;
              break;
            case 'video':
              assetTypes.video++;
              break;
            case 'document':
              assetTypes.documents++;
              break;
            default:
              assetTypes.other++;
          }
        }
      }

      return {
        valid: errors.length === 0,
        errors,
        warnings,
        metadata: {
          fileCount,
          directoryCount,
          totalSize,
          hasReadme: readmeFile !== null,
          assetTypes,
        },
      };
    } catch (error) {
      errors.push({
        code: 'INVALID_ZIP',
        message: error instanceof Error ? error.message : 'Failed to parse ZIP file',
        severity: 'error',
      });

      return {
        valid: false,
        errors,
        warnings,
        metadata: {
          fileCount: 0,
          directoryCount: 0,
          totalSize: 0,
          hasReadme: false,
          assetTypes: { images: 0, audio: 0, video: 0, documents: 0, other: 0 },
        },
      };
    }
  }

  resolveAssetPath(markdownPath: string, assetPath: string): string {
    // Normalize paths
    markdownPath = markdownPath.replace(/\\/g, '/');
    assetPath = assetPath.replace(/\\/g, '/');

    // Handle absolute paths
    if (assetPath.startsWith('/')) {
      return assetPath;
    }

    // Get directory of markdown file
    let baseDir = markdownPath.substring(0, markdownPath.lastIndexOf('/'));
    if (!baseDir) {
      baseDir = '';
    }

    // Start with base directory parts
    const baseParts = baseDir.split('/').filter(p => p !== '');
    const assetParts = assetPath.split('/');

    // Apply asset path parts to base
    for (const part of assetParts) {
      if (part === '..') {
        if (baseParts.length > 0) {
          baseParts.pop();
        }
      } else if (part !== '.' && part !== '') {
        baseParts.push(part);
      }
    }

    return '/' + baseParts.join('/');
  }
}
